import { FC, useEffect } from "react";
import classNames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import ReactMarkdown from 'react-markdown';

import { selectResponseModal, selectResponses } from "../../store/selectors/responses";
import { clearResponseModal } from "../../store/response/slice";
import { selectModelsState } from "../../store/selectors/models";
import { selectActivePromptsWithScoresState } from "../../store/selectors/prompts";
import { EyeIcon, SentimentPositiveIcon, TrophyIcon } from "../icons";
import NumberValue from "../Table/components/ValueRenderers/NumberValue";

import styles from "./ModalResponse.module.css";



const ModalResponse: FC = () => {
  const dispatch = useDispatch();
  const responseModalData = useSelector(selectResponseModal);
  const responses = useSelector(selectResponses);
  const models = useSelector(selectModelsState);
  const prompts = useSelector(selectActivePromptsWithScoresState);
  const response = responseModalData?.responseId && responses.find((response) => response.id === responseModalData.responseId);

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(clearResponseModal());
      }
    };

    if (responseModalData) {
      document.addEventListener('keydown', handleEscKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [responseModalData, dispatch]);

  if (!responseModalData || !response) return null;

  const prompt = prompts?.find((prompt) => prompt.id === response?.prompt_id);
  const model = models?.find((model) => model.id === response?.model_id);
  const isMentioned = response.is_mentioned;

  const visibility = responseModalData.metrics?.visibility ?? response.value;
  const sentiment = responseModalData.metrics?.sentiment;
  const ranking = responseModalData.metrics?.ranking;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      dispatch(clearResponseModal());
    }
  };



  return (
    <div className={styles['modal']} onClick={handleOverlayClick}>
      <div className={classNames(styles['modal__wrapper'], !isMentioned && styles['is-mentions'])}>
        <button className={styles['modal__close']} onClick={() => { dispatch(clearResponseModal()); }}>✕</button>
        <h3 className={styles['modal__title']}>{prompt?.content}</h3>
        <div className={styles['modal__metrics']}>
          <div className={classNames(styles['modal__metric'], styles['modal__metric--visibility'])}>
            <EyeIcon aria-label="Visibility" />
            {visibility === 1 ? 'yes' : 'no'}
          </div>
          <div className={classNames(styles['modal__metric'], styles['modal__metric--sentiment'])}>
            <SentimentPositiveIcon aria-label="Sentiment" />
            <NumberValue value={sentiment} loader={false} />
          </div>
          <div className={classNames(styles['modal__metric'], styles['modal__metric--ranking'])}>
            <TrophyIcon aria-label="Ranking" />
            <NumberValue value={ranking} loader={false} />
          </div>
        </div>
        <div className={styles['modal__body']}>
          <h4 className={styles['modal__caption']}>{model?.name} {model?.data?.version}:</h4>
          <div className={classNames(styles.markdown, styles['modal__content'])}>
            <ReactMarkdown>{response.content}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalResponse;